<template>
  <div :class="['app-frame', { 'app-frame--workspace': isWorkspaceRoute }]">
    <AppTopBar
      :current-user="auth.currentUser.value"
      :member-label="memberLabel"
      @logout="handleLogout"
      @open-auth="handleOpenAuth"
    />

    <main :class="['page-shell', { 'page-shell--workspace': isWorkspaceRoute }]">
      <RouterView />
    </main>

    <el-dialog
      v-model="auth.authDialogVisible.value"
      width="860px"
      destroy-on-close
      align-center
      class="auth-modal"
      :show-close="true"
    >
      <AuthView
        embedded
        :initial-tab="auth.authDialogInitialTab.value"
        :redirect="auth.authDialogRedirect.value"
        @authenticated="handleAuthSuccess"
      />
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import AppTopBar from './components/AppTopBar.vue'
import AuthView from './views/AuthView.vue'
import { useAuth } from './auth'
import { useDashboardData } from './composables/useDashboardData'

const auth = useAuth()
const { dashboard } = useDashboardData()
const route = useRoute()
const router = useRouter()

const isWorkspaceRoute = computed(() => route.path === '/review')

const memberLabel = computed(() => {
  const status = dashboard.value?.member_status ?? auth.currentUser.value?.member_status ?? 'free'
  if (status === 'monthly') return '月会员'
  if (status === 'quarterly') return '季会员'
  if (status === 'yearly') return '年会员'
  return '普通用户'
})

function handleLogout() {
  auth.logout()
  void router.push('/')
}

function handleOpenAuth() {
  auth.openAuthDialog({ redirect: route.fullPath || '/' })
}

async function handleAuthSuccess(target: string) {
  auth.closeAuthDialog()
  if (target && target !== route.fullPath) {
    await router.replace(target)
  }
}
</script>

<style scoped>
.app-frame {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  isolation: isolate;
}

.app-frame::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    radial-gradient(circle at 12% 8%, rgba(37, 99, 255, 0.08), transparent 24%),
    radial-gradient(circle at 88% 18%, rgba(34, 211, 238, 0.08), transparent 20%);
}

.page-shell {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 20px 12px 18px;
}

.app-frame--workspace {
  height: 100vh;
  overflow: hidden;
}

.page-shell--workspace {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

:deep(.auth-modal .el-dialog) {
  width: min(860px, 92vw);
  max-width: 860px;
  border-radius: 32px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background:
    radial-gradient(circle at top left, rgba(37, 99, 255, 0.14), transparent 30%),
    radial-gradient(circle at bottom right, rgba(34, 211, 238, 0.12), transparent 32%),
    rgba(255, 255, 255, 0.94);
  box-shadow: 0 32px 80px rgba(15, 23, 42, 0.24);
  backdrop-filter: blur(18px);
}

:deep(.auth-modal .el-dialog__header) {
  margin: 0;
  padding: 0;
}

:deep(.auth-modal .el-dialog__body) {
  padding: 0;
}

:deep(.auth-modal .el-dialog__headerbtn) {
  top: 18px;
  right: 18px;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

:deep(.auth-modal .el-dialog__headerbtn:hover) {
  background: #ffffff;
}

:deep(.auth-modal .el-overlay) {
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(8px);
}

@media (max-width: 768px) {
  :deep(.auth-modal .el-dialog) {
    width: calc(100vw - 20px);
    margin: 10px auto;
    border-radius: 24px;
  }
}
</style>